import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';
import Workflow from '../components/Workflow';
import ScreenshotGallery from '../components/ScreenshotGallery';
import TestimonialBlock from '../components/TestimonialBlock';

const HS_SHOTS = [
  { src: '/images/healthcare/hc-portfolio-tracker-hero.jpg', caption: 'Videra Hard Services portfolio dashboard — live PFI hard-services rollup.' },
  { src: '/images/healthcare/hc-pmo.jpg', caption: 'PMO view — every lifecycle intervention, every project, every gate.' },
  { src: '/images/healthcare/hc-asset-register.jpg', caption: 'Asset register linked to lifecycle plans and interventions.' },
  { src: '/images/healthcare/hc-handback-readiness.jpg', caption: 'Progressive handback readiness — evidence position by asset and site.' },
  { src: '/images/healthcare/hc-weekly-governance.jpg', caption: 'Weekly governance meeting — driven from live delivery data.' },
  { src: '/images/healthcare/hc-projects-hs-gate.jpg', caption: 'Hard Services gate approvals with role-based sign-off.' },
  { src: '/images/healthcare/hc-projects-commercial.jpg', caption: 'Commercial trail for variations — uplift logic and evidence in one place.' },
];

const V2VideraHardServices = () => {
  useSEO({
    title: 'Videra Hard Services | Project, lifecycle & handback',
    description: 'Project delivery, lifecycle management and progressive handback readiness for PFI and complex Hard Services environments. In live PFI use.',
    path: '/videra/hard-services',
  });

  return (
    <div data-testid="v2-videra-hard-services">
      <ProductHero
        eyebrow="Videra Hard Services"
        title="Control today's delivery. Build tomorrow's readiness."
        subtitle="Project delivery, lifecycle management and progressive handback readiness for complex Hard Services environments."
        body={(
          <>
            <p>Videra Hard Services brings lifecycle requirements, projects, governance, approvals, evidence and operational delivery into one structured environment.</p>
            <p>Developed from practical experience of complex PFI and healthcare estates, the platform is designed around the realities of managing work within live operational environments.</p>
            <p className="text-slate-600 italic">In live use within PFI Hard Services environments involving Tier-1 contractors supporting hospital estates in England.</p>
          </>
        )}
        primary={{ label: 'Try Videra Hard Services — 24 days', href: 'https://demo.videra.app/explore' }}
        secondary={{ label: 'Talk to Keystone', to: '/contact' }}
        heroImage="/images/healthcare/hc-portfolio-tracker-hero.jpg"
        heroImageAlt="Videra Hard Services portfolio dashboard"
        testid="hard-services-hero"
      />

      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">The Hard Services journey</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-10">Requirement to handback readiness, connected.</h2>
          <Workflow testid="hard-services-workflow" steps={['Requirement','Assess','Prioritise','Lifecycle','Project','Approval','Delivery','Evidence','Asset position','Handback readiness']} />
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            Every completed intervention should improve the lifecycle position.
          </h2>
          <div className="mt-8 text-base lg:text-lg text-slate-700 leading-relaxed space-y-4">
            <p>Lifecycle and project information should not disappear into closed project folders.</p>
            <p>Videra connects the reason for intervention with the project that delivers it, the evidence created and the resulting lifecycle position.</p>
          </div>
          <div className="mt-10">
            <a href="https://demo.videra.app/explore" className="inline-flex items-center gap-2 bg-slate-900 text-white px-7 py-3.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors">
              Try Videra Hard Services — 24 days <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <ScreenshotGallery
        eyebrow="Inside Videra Hard Services"
        title="What the daily operating environment looks like."
        description="Screens from the live PFI Hard Services environment used by Tier-1 contractors supporting UK hospital estates."
        shots={HS_SHOTS}
        testid="hard-services-gallery"
      />

      <TestimonialBlock product="videra-hard-services" />
    </div>
  );
};

export default V2VideraHardServices;
